import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Autocomplete
} from '@mui/material';
import { 
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { 
  TASK_TYPES, 
  ENTITY_TYPES, 
  PRIORITIES,
  TASK_STATUSES,
  CREATED_BY_TYPES
} from '../../constants/tasksConstants';

const TasksFilters = ({ filters, onFilterChange, employees, clients, leads, isAdmin, currentUserId }) => {
  const [expanded, setExpanded] = useState(false);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    onFilterChange({
      ...filters,
      [name]: value
    });
  };

  const handleEntityChange = (event, value) => {
    onFilterChange({
      ...filters,
      entityId: value ? value.id : 'all'
    });
  };

  const handleResetFilters = () => {
    onFilterChange({
      search: '',
      assignedTo: isAdmin ? 'all' : currentUserId,
      entityType: 'all',
      taskType: 'all',
      priority: 'all',
      status: 'all',
      createdBy: 'all',
      entityId: 'all',
      dateFrom: '',
      dateTo: ''
    });
  };

  // Przygotowanie opcji dla autocomplete
  const getEntityOptions = () => {
    if (filters.entityType === 'client') {
      return clients.map(client => ({
        id: client.id,
        label: `${client.firstName} ${client.lastName}`
      }));
    } else if (filters.entityType === 'lead') {
      return leads.map(lead => ({
        id: lead.id,
        label: `${lead.firstName} ${lead.lastName}`
      }));
    } else {
      // Połącz obie listy, gdy wybrano "Wszystkie typy"
      const clientOptions = clients.map(client => ({
        id: client.id,
        label: `${client.firstName} ${client.lastName} (Klient)`
      }));
      
      const leadOptions = leads.map(lead => ({
        id: lead.id,
        label: `${lead.firstName} ${lead.lastName} (Lead)`
      }));
      
      return [...clientOptions, ...leadOptions];
    }
  };

  return (
    <Accordion 
      expanded={expanded} 
      onChange={() => setExpanded(!expanded)}
      sx={{ mb: 3 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FilterIcon sx={{ mr: 1 }} />
          <Typography>Filtry i wyszukiwanie</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {/* Wyszukiwanie */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              name="search"
              label="Wyszukaj"
              value={filters.search}
              onChange={handleFilterChange}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              placeholder="Opis zadania, szczegóły..."
            />
          </Grid>

          {/* Filtr przypisanego pracownika (tylko dla admina) */}
          {isAdmin && (
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Przypisane do</InputLabel>
                <Select
                  name="assignedTo"
                  value={filters.assignedTo}
                  onChange={handleFilterChange}
                  label="Przypisane do"
                >
                  <MenuItem value="all">Wszyscy pracownicy</MenuItem>
                  {employees.map(employee => (
                    <MenuItem key={employee.id} value={employee.id}>
                      {`${employee.firstName || ''} ${employee.lastName || ''}`.trim() || employee.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Filtr statusu */}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Status"
              >
                <MenuItem value="all">Wszystkie statusy</MenuItem>
                {Object.entries(TASK_STATUSES).map(([key, value]) => (
                  <MenuItem key={key} value={key}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Filtr priorytetu */}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Priorytet</InputLabel>
              <Select
                name="priority"
                value={filters.priority}
                onChange={handleFilterChange}
                label="Priorytet"
              >
                <MenuItem value="all">Wszystkie priorytety</MenuItem>
                {Object.entries(PRIORITIES).map(([key, value]) => (
                  <MenuItem key={key} value={key}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Filtr typu zadania */}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Typ zadania</InputLabel>
              <Select
                name="taskType"
                value={filters.taskType}
                onChange={handleFilterChange}
                label="Typ zadania"
              >
                <MenuItem value="all">Wszystkie typy</MenuItem>
                {Object.entries(TASK_TYPES).map(([key, value]) => (
                  <MenuItem key={key} value={key}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Filtr typu podmiotu */}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Typ podmiotu</InputLabel>
              <Select
                name="entityType"
                value={filters.entityType}
                onChange={handleFilterChange}
                label="Typ podmiotu"
              >
                <MenuItem value="all">Wszystkie typy</MenuItem>
                {Object.entries(ENTITY_TYPES).map(([key, value]) => (
                  <MenuItem key={key} value={key}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Filtr utworzone przez */}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Utworzone przez</InputLabel>
              <Select
                name="createdBy"
                value={filters.createdBy}
                onChange={handleFilterChange}
                label="Utworzone przez"
              >
                <MenuItem value="all">Wszystkie źródła</MenuItem>
                <MenuItem value="me">Mnie</MenuItem>
                <MenuItem value="ai_system">System AI</MenuItem>
                <MenuItem value="other">Inne osoby</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Filtr konkretnego klienta/leada */}
          <Grid item xs={12} md={3}>
            <Autocomplete
              size="small"
              options={getEntityOptions()}
              onChange={handleEntityChange}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Wybierz podmiot" 
                  placeholder="Wyszukaj klienta lub leada"
                  fullWidth
                />
              )}
            />
          </Grid>

          {/* Filtr zakresu dat */}
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              name="dateFrom"
              label="Data od"
              type="date"
              value={filters.dateFrom}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              name="dateTo"
              label="Data do"
              type="date"
              value={filters.dateTo}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Przycisk resetowania filtrów */}
          <Grid item xs={12} md={2}>
            <Button 
              variant="outlined" 
              onClick={handleResetFilters}
              fullWidth
            >
              Resetuj filtry
            </Button>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default TasksFilters;
